/**
 * A video on a registration page. The page's writer never writes a <video> or a
 * script: it gives one element `data-video="<note id>"`, styled in the page's own
 * colours (its frame, its corners, its shadow), holding a play button marked
 * `data-video-play`. Each time the page is served (and in the editor's preview),
 * this fills that element with the video, its poster and a fresh token, and adds
 * the small player that makes the button play it. The look stays the page's.
 *
 * A page video is always an adaptive stream (`hls/<id>/master.m3u8`), like a
 * webinar's talk, so a phone on a slow connection starts on a light rung. An
 * Apple device plays the stream itself, inside the tap, so it plays with sound;
 * elsewhere hls.js is fetched from the app, only when someone moves toward the
 * play button (some Android browsers claim the stream and play it badly), and
 * never takes a rung wider than the frame it plays in (at up to twice its
 * pixels), so a phone on fast Wi-Fi does not pull 1080p for a 340 px frame.
 * Full screen and a resize measure again, and a bigger frame drops what was
 * buffered ahead at the smaller rung, or a short video would stay small to the
 * end. Why measured here: hls.js 1.7's own capLevelToPlayerSize measures once,
 * before the rungs are known, and never again unless the player changes size,
 * so it never capped.
 */

/** One playable page video: its address and poster, each carrying its token. */
export interface PageVideo {
  src: string;
  poster: string | null;
  /** An adaptive stream (a master playlist) rather than one file. */
  stream: boolean;
}

/** The most videos one page carries. */
const PAGE_VIDEO_MAX = 4;

/** A note id as the page's writer is given it. */
export const PAGE_VIDEO_ID = /^note_[A-Za-z0-9_-]+$/;

/** An opening tag that carries `data-video="note_…"`. */
const MARK = /<[a-z][a-z0-9-]*\b[^>]*\bdata-video\s*=\s*"(note_[A-Za-z0-9_-]+)"[^>]*>/gi;

/** The videos a page asks for, in order, each once. */
export function pageVideoIds(html: string): string[] {
  const out: string[] = [];
  for (const m of html.matchAll(MARK)) {
    if (!out.includes(m[1]) && out.length < PAGE_VIDEO_MAX) out.push(m[1]);
  }
  return out;
}

const attr = (v: string) => v.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");

function videoTag(v: PageVideo): string {
  return (
    `<video data-fr-video preload="none" playsinline webkit-playsinline` +
    `${v.poster ? ` poster="${attr(v.poster)}"` : ""}` +
    ` data-src="${attr(v.src)}" data-stream="${v.stream ? "1" : "0"}"></video>`
  );
}

/*
 * The page's own rules win wherever they speak (every default here has no
 * weight, through :where); only the rules that must hold whatever the page
 * says carry it: the button goes once the video plays, and the video comes up
 * over the frame.
 */
const CSS =
  ":where([data-video]){position:relative;overflow:hidden;aspect-ratio:16/9;cursor:pointer}" +
  ":where([data-video])>video[data-fr-video]{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;border-radius:inherit;z-index:0}" +
  ":where([data-video]) :where([data-video-play],.fr-video-play){position:relative;z-index:1;cursor:pointer}" +
  "[data-video].fr-on [data-video-play],[data-video].fr-on .fr-video-play{display:none}" +
  "[data-video].fr-on>video[data-fr-video]{z-index:2;object-fit:contain;background:#000}" +
  "[data-video].fr-on{cursor:auto}" +
  ".fr-video-play{position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);width:72px;height:72px;padding:0;border:0;" +
  "border-radius:50%;background:var(--accent,#1a1a1a);box-shadow:0 10px 28px rgba(0,0,0,.28)}" +
  ".fr-video-play::after{content:\"\";position:absolute;left:29px;top:22px;border-left:20px solid var(--paper,#fff);" +
  "border-top:14px solid transparent;border-bottom:14px solid transparent}" +
  "@media (prefers-reduced-motion:no-preference){.fr-video-play,[data-video-play]{transition:transform .15s ease}}";

/*
 * The player. A tap anywhere on the frame plays, with sound, since the tap is
 * the viewer's own. hls.js is fetched once, when a pointer or a finger first
 * comes near a frame, so the page itself stays light.
 */
function playerScript(lib: string): string {
  return `(function(){
var LIB=${JSON.stringify(lib)},ready=null;
function hls(){if(!ready)ready=new Promise(function(res){if(window.Hls)return res(window.Hls);var s=document.createElement("script");s.src=LIB;s.async=true;s.onload=function(){res(window.Hls||null)};s.onerror=function(){res(null)};document.head.appendChild(s)});return ready}
function native(v){return /Apple/.test(navigator.vendor||"")&&!!v.canPlayType("application/vnd.apple.mpegurl")}
function go(v){var p=v.play();if(p&&p.catch)p.catch(function(){})}
function cap(h,v){var L=h.levels||[];if(!L.length)return;var w=v.getBoundingClientRect().width*Math.min(window.devicePixelRatio||1,2),i=L.length-1;for(var j=0;j<L.length;j++){if(L[j].width>=w){i=j;break}}var was=h.autoLevelCapping;h.autoLevelCapping=i;if(was>=0&&i>was&&h.autoLevelEnabled)h.nextLevel=-1}
function play(box){var v=box.querySelector("video[data-fr-video]");if(!v||box.classList.contains("fr-on"))return;box.classList.add("fr-on");v.controls=true;var src=v.getAttribute("data-src");
if(v.getAttribute("data-stream")!=="1"){v.src=src;go(v);return}
if(native(v)){v.crossOrigin="use-credentials";v.src=src;go(v);return}
hls().then(function(H){if(!H||!H.isSupported()){v.crossOrigin="use-credentials";v.src=src;go(v);return}var h=new H({xhrSetup:function(x){x.withCredentials=true}});var fit=function(){cap(h,v)};h.on(H.Events.MANIFEST_PARSED,function(){fit();go(v)});window.addEventListener("resize",fit);document.addEventListener("fullscreenchange",fit);document.addEventListener("webkitfullscreenchange",fit);h.loadSource(src);h.attachMedia(v)})}
function wire(){document.querySelectorAll("[data-video]").forEach(function(box){var v=box.querySelector("video[data-fr-video]");if(!v||box.getAttribute("data-fr-wired"))return;box.setAttribute("data-fr-wired","1");
if(!box.querySelector("[data-video-play]")){var b=document.createElement("button");b.type="button";b.className="fr-video-play";b.setAttribute("aria-label","Play the video");box.appendChild(b)}
box.addEventListener("click",function(e){if(box.classList.contains("fr-on"))return;e.preventDefault();play(box)});
var warm=function(){if(v.getAttribute("data-stream")==="1"&&!native(v))hls()};box.addEventListener("pointerenter",warm,{once:true});box.addEventListener("touchstart",warm,{once:true,passive:true})})}
if(document.readyState==="loading")document.addEventListener("DOMContentLoaded",wire);else wire()})();`;
}

/**
 * The page with each of its known videos in place, and the player in its head.
 * An element whose video is not known (not ready yet, or not this workspace's)
 * is left exactly as the page drew it.
 */
export function fillPageVideos(html: string, videos: Readonly<Record<string, PageVideo>>, lib: string): string {
  let used = false;
  const filled = html.replace(MARK, (open: string, id: string) => {
    const v = videos[id];
    if (!v) return open;
    used = true;
    return `${open}${videoTag(v)}`;
  });
  if (!used) return filled;
  const tags = `<style data-fr-video>${CSS}</style><script data-fr-video>${playerScript(lib)}</script>`;
  const closes = /<\/head\s*>/i;
  return closes.test(filled) ? filled.replace(closes, () => `${tags}</head>`) : `${tags}${filled}`;
}
